import { client, handleRequest, handleRequestBody } from "./client.ts";

export type PostStatus = "draft" | "published";
export type PostWeather = "sun" | "cloud" | "cloud-sun" | "cloud-rain";
export type PostVisibility = "all" | "public" | "internal";

export type PostMediaType = "image" | "video" | "photo_360";

export type PostMediaProcessingStatus = "pending" | "ready" | "failed";

export interface PostMedia {
	id: string;
	type: PostMediaType;
	url: string;
	caption?: string;
	position: number;
	processing_status: PostMediaProcessingStatus;
	thumbnail_url?: string;
	duration_ms?: number;
	width?: number;
	height?: number;
}

export interface Author {
	id?: string;
	name?: string;
	role?: string;
	removed: boolean;
}

export interface PostStage {
	id: string;
	name: string;
}

export interface PostChainEntry {
	id: string;
	title: string;
	occurred_at: string;
}

export interface PostReactions {
	count: number;
	reacted: boolean;
	type?: string | null;
	types?: string[];
}

export interface PostCommentMention {
	id: string;
	name: string;
}

export interface PostComment {
	id: string;
	post_id: string;
	author: Author;
	body: string;
	mentions: PostCommentMention[];
	created_at: string;
}

export interface Post {
	id: string;
	account_id: string;
	worksite_id?: string;
	construction_id?: string;
	author: Author;
	status: PostStatus;
	title: string;
	body: string;
	internal: boolean;
	weather_condition?: PostWeather;
	occurred_at: string;
	published_at?: string;
	created_at: string;
	updated_at: string;
	media: PostMedia[];
	stage?: PostStage;
	parent_post_id?: string;
	chain?: PostChainEntry[];
	reactions: PostReactions;
	comment_count: number;
}

export interface ListPostsParams {
	page?: number;
	pageSize?: number;
	worksiteId?: string;
	constructionId?: string;
	visibility?: PostVisibility;
	stageId?: string;
	occurredFrom?: string;
	occurredTo?: string;
	authorUserId?: string;
}

export interface ListPostsResponse {
	count: number;
	items: Post[];
}

export function listPosts(
	params: ListPostsParams = {},
): Promise<ListPostsResponse> {
	return handleRequestBody(() =>
		client.get<ListPostsResponse>("/posts", {
			params: {
				page: params.page,
				page_size: params.pageSize,
				worksite_id: params.worksiteId,
				construction_id: params.constructionId,
				visibility: params.visibility,
				stage_id: params.stageId,
				occurred_from: params.occurredFrom,
				occurred_to: params.occurredTo,
				author_user_id: params.authorUserId,
			},
		}),
	);
}

export interface CreatePostInput {
	worksiteId?: string;
	constructionId?: string;
	status?: PostStatus;
	title: string;
	body: string;
	internal?: boolean;
	weatherCondition?: PostWeather;
	stageId?: string;
	parentPostId?: string;
	media?: File[];
}

interface PostResponse {
	post: Post;
}

export async function getPost(id: string): Promise<Post> {
	const { post } = await handleRequestBody(() =>
		client.get<PostResponse>(`/posts/${id}`),
	);
	return post;
}

export async function createPost(input: CreatePostInput): Promise<Post> {
	const data = {
		worksite_id: input.worksiteId,
		construction_id: input.constructionId,
		status: input.status,
		title: input.title,
		body: input.body,
		internal: input.internal,
		weather_condition: input.weatherCondition,
		stage_id: input.stageId,
		parent_post_id: input.parentPostId,
	};

	if (input.media && input.media.length > 0) {
		const form = new FormData();
		form.append("data", JSON.stringify(data));
		for (const item of input.media) {
			form.append("media", item);
		}
		const { post } = await handleRequestBody(() =>
			client.post<PostResponse>("/posts", form),
		);
		return post;
	}

	const { post } = await handleRequestBody(() =>
		client.post<PostResponse>("/posts", data),
	);
	return post;
}

export interface UpdatePostInput {
	title?: string;
	body?: string;
	status?: PostStatus;
	internal?: boolean;
	weatherCondition?: PostWeather;
	stageId?: string;
}

export async function updatePost(
	id: string,
	input: UpdatePostInput,
): Promise<Post> {
	const { post } = await handleRequestBody(() =>
		client.patch<PostResponse>(`/posts/${id}`, {
			title: input.title,
			body: input.body,
			status: input.status,
			internal: input.internal,
			weather_condition: input.weatherCondition,
			stage_id: input.stageId,
		}),
	);
	return post;
}

export async function deletePost(id: string): Promise<void> {
	await handleRequest(() => client.delete(`/posts/${id}`));
}

export interface ReactResponse {
	reacted: boolean;
	type: string | null;
	count: number;
	types: string[];
}

export function react(postId: string, type: string): Promise<ReactResponse> {
	return handleRequestBody(() =>
		client.post<ReactResponse>(`/posts/${postId}/reaction`, { type }),
	);
}

export interface ListCommentsResponse {
	count: number;
	items: PostComment[];
}

export function listComments(postId: string): Promise<ListCommentsResponse> {
	return handleRequestBody(() =>
		client.get<ListCommentsResponse>(`/posts/${postId}/comments`),
	);
}

export interface CreateCommentInput {
	body: string;
}

interface CommentResponse {
	comment: PostComment;
}

export async function createComment(
	postId: string,
	input: CreateCommentInput,
): Promise<PostComment> {
	const { comment } = await handleRequestBody(() =>
		client.post<CommentResponse>(`/posts/${postId}/comments`, {
			body: input.body,
		}),
	);
	return comment;
}

export async function deleteComment(
	postId: string,
	commentId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(`/posts/${postId}/comments/${commentId}`),
	);
}
